import * as support from "./AggregatedClaimsTable.support";
import {
  AddRounded as AddIcon,
  DeleteRounded as DeleteIcon,
  MoreVertRounded as MoreIcon,
  SaveAltRounded as DownloadIcon,
} from "@mui/icons-material";
import {
  Checkbox,
  Divider,
  FormControlLabel,
  ListItem,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import { DataGrid, GridActionsCellItem, useGridApiRef } from "@mui/x-data-grid";
import Component from "components/Component";
import Button from "components/common/Button";
import IconButton from "components/common/IconButton";
import { makePrettyNumberColDef } from "components/common/dataGrid/prettyNumberColumn";
import * as labels from "config.labels";
import { useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as claimsActions from "store/actions/input/claims/claimsActions";
import { useTranslator } from "store/hooks/staticDataHooks";
import * as claimsSelectors from "store/selectors/input/claims/claimsSelectors";
import * as exposureSelectors from "store/selectors/input/exposure/exposureSelectors";
import * as programSelectors from "store/selectors/input/program/programSelectors";

const BlankSlate = ({ title, onAddManually }) => (
  <Component
    title={title}
    options={<Button onClick={onAddManually}>{"Add Manually"}</Button>}
  >
    <Stack alignItems={"center"} justifyContent={"center"}>
      <Typography sx={{ padding: "5rem", color: "gray !important" }}>
        {"No claims loaded at present"}
      </Typography>
    </Stack>
  </Component>
);

const CoveragePicker = ({ lossCategories, coverages, onChange, onCancel }) => {
  const [localCoverages, setLocalCoverages] = useState(coverages);
  const noneSelected = localCoverages.length === 0;

  const updateLocalCoverages = (coverage) => {
    localCoverages.includes(coverage)
      ? setLocalCoverages(localCoverages.filter((x) => x !== coverage))
      : setLocalCoverages([...localCoverages, coverage]);
  };

  return (
    <Stack className={"fade-in"}>
      <Typography marginBottom={1}>
        {"Please choose one or more coverages:"}
      </Typography>
      {lossCategories.map((coverage) => (
        <FormControlLabel
          key={coverage.key}
          control={<Checkbox checked={localCoverages.includes(coverage.key)} />}
          label={coverage.display}
          onChange={(_) => updateLocalCoverages(coverage.key)}
        />
      ))}

      <Stack direction={"row"} gap={1} marginTop={2}>
        <Button
          onClick={() => onChange(localCoverages)}
          isDisabled={noneSelected}
          sx={{ minWidth: "6rem" }}
        >
          {"Done"}
        </Button>
        <Button
          onClick={onCancel}
          color={"secondary"}
          sx={{ minWidth: "6rem" }}
        >
          {"Cancel"}
        </Button>
      </Stack>
    </Stack>
  );
};

const MoreMenu = ({ onDownload, onDelete }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const openMenu = (e) => setAnchorEl(e.currentTarget);
  const closeMenu = () => setAnchorEl(null);

  return (
    <>
      <IconButton icon={MoreIcon} onClick={openMenu} />
      <Menu
        anchorEl={anchorEl}
        open={anchorEl != null}
        onClose={(_, reason) => {
          if (reason === "backdropClick") closeMenu();
        }}
      >
        <MenuItem onClick={onDownload}>
          <ListItem>{"Download"}</ListItem>
          <ListItemIcon>
            <DownloadIcon />
          </ListItemIcon>
        </MenuItem>
        <Divider />
        <MenuItem onClick={onDelete}>
          <ListItem>{"Delete"}</ListItem>
          <ListItemIcon>
            <DeleteIcon />
          </ListItemIcon>
        </MenuItem>
      </Menu>
    </>
  );
};

const makeColumns = (onDeleteRow) => [
  { field: "year", headerName: "Period", width: 100, sortable: false },
  makePrettyNumberColDef({
    field: "count",
    headerName: "№ of Claims",
    width: 100,
    dp: 0,
    sortable: false,
  }),
  makePrettyNumberColDef({
    field: "paid",
    headerName: "Paid",
    flex: 1,
    prefix: "$",
    dp: 0,
    sortable: false,
  }),
  makePrettyNumberColDef({
    field: "reserved",
    headerName: "Reserved",
    flex: 1,
    prefix: "$",
    dp: 0,
    sortable: false,
  }),
  makePrettyNumberColDef({
    field: "incurred",
    headerName: "Incurred",
    flex: 1,
    prefix: "$",
    dp: 0,
    sortable: false,
  }),
  {
    field: "actions",
    type: "actions",
    width: 50,
    getActions: (params) => [
      <GridActionsCellItem
        icon={<DeleteIcon />}
        label={"Delete row"}
        onClick={() => onDeleteRow(params.row.year)}
      />,
    ],
  },
];

const Editor = ({
  lossCategories,
  coverage,
  data,
  onChange,
  onChangeRow,
  onDeleteRow,
  onAddRow,
}) => {
  const apiRef = useGridApiRef();
  const pasteRef = useRef(false);
  const [activeCell, setActiveCell] = useState({ rowId: null, field: null });
  const columns = makeColumns(onDeleteRow);
  const editableFields = columns
    .filter((c) => c.field !== "year")
    .map((c) => c.field);

  const parseNumber = (value) => {
    if (value === "") return null;
    const parsed = Number(value.replace(/[$,%\s,]/g, ""));
    return isFinite(parsed) ? parsed : value;
  };

  const handlePaste = (e) => {
    if (!activeCell.field || activeCell.field === "year") return;

    const text = e.clipboardData.getData("text");
    if (!text) return;

    const startCol = editableFields.indexOf(activeCell.field);
    if (startCol === -1) return;

    const rows = text
      .trimEnd()
      .split(/\r?\n/)
      .map((row) => row.split("\t"));

    if (rows.length < 1 || rows[0].length < 2) return;

    e.preventDefault();
    pasteRef.current = true;

    const updated = [...data];
    rows.forEach((row, rowIndex) => {
      const targetRowId = activeCell.rowId + rowIndex;
      if (targetRowId >= updated.length) return;
      const updatedRow = { ...updated[targetRowId] };
      row.forEach((cell, cellIndex) => {
        const field = editableFields[startCol + cellIndex];
        if (!field) return;
        updatedRow[field] = parseNumber(cell);
      });
      updated[targetRowId] = updatedRow;
    });

    onChange(updated);

    // Ensure the internal edit cell reflects the
    // pasted value before leaving edit mode
    if (apiRef.current) {
      const cellValue = updated[activeCell.rowId]?.[activeCell.field];
      if (cellValue !== undefined && apiRef.current.setEditCellValue) {
        apiRef.current.setEditCellValue({
          id: activeCell.rowId,
          field: activeCell.field,
          value: cellValue,
          debounceMs: 0,
        });
      }
      if (apiRef.current.stopCellEditMode) {
        apiRef.current.stopCellEditMode({
          id: activeCell.rowId,
          field: activeCell.field,
        });
      }
    }
    // Allow DataGrid time to finish its internal
    // commit cycle before enabling onChangeRow again
    setTimeout(() => {
      pasteRef.current = false;
    }, 0);
  };

  const handleCellEdit = (newRow, _) => {
    if (!pasteRef.current) {
      // Ignore row updates triggered by paste
      const { id, ...rest } = newRow;
      onChangeRow(rest);
    }
    return newRow;
  };

  const label = support.coverageLabel(lossCategories, coverage);
  const rows = data.map((row, index) => ({ id: index, ...row }));

  return (
    <Stack className={"fade-in-slow"} onPaste={handlePaste}>
      <Typography variant={"subtitle2"}>{label}</Typography>
      <DataGrid
        rows={rows}
        columns={columns}
        autoHeight
        disableSelectionOnClick
        hideFooter
        processRowUpdate={handleCellEdit}
        disableColumnMenu
        apiRef={apiRef}
        onCellClick={(params) => {
          setActiveCell({ rowId: Number(params.id), field: params.field });
        }}
      />
      <IconButton
        icon={AddIcon}
        onClick={onAddRow}
        sx={{ alignSelf: "flex-end", width: 50 }}
      />
    </Stack>
  );
};

const AggregatedClaimsInput = () => {
  const dispatch = useDispatch();
  const translateLabel = useTranslator("uiLabels");

  const lossCategories =
    useSelector(exposureSelectors.selectLossCategories) ?? [];
  const activeCoverages = useSelector(
    claimsSelectors.selectAggregatedClaimsCoverages
  );
  const aggregatedClaims = useSelector(claimsSelectors.selectAggregatedClaims);
  const usingAggregatedClaimsInput = aggregatedClaims.manualMode ?? false;
  const data = aggregatedClaims?.data ?? [];
  const insured = useSelector(programSelectors.getInsured);
  const inception = useSelector(programSelectors.getInception);

  const [coverages, setCoverages] = useState({
    isEditing: false,
    active: activeCoverages,
  });

  const title = translateLabel(labels.AGGREGATED_CLAIMS_TABLE_TITLE, {
    default: "Aggregated Claims",
  });

  const handleAddManually = () => {
    dispatch(
      claimsActions.updateAggregatedClaims({ manualMode: true, data: [] })
    );
    setCoverages({ isEditing: true, active: [] });
  };

  const handleUpdateCoverages = (coverages) => {
    const updated = support.applyCoverages(coverages, data, {
      endDate: inception,
    });
    dispatch(claimsActions.updateAggregatedClaims({ data: updated }));
    setCoverages({ isEditing: false, active: coverages });
  };

  const handleUpdateData = (coverage, newData) => {
    const updated = data.map((item) =>
      item.coverage === coverage ? { coverage, data: newData } : item
    );
    dispatch(claimsActions.updateAggregatedClaims({ data: updated }));
  };

  const handleUpdateRow = (coverage, newRow) => {
    const updated = support.applyRowUpdate(coverage, data, newRow);
    dispatch(claimsActions.updateAggregatedClaims({ data: updated }));
  };

  const handleDeleteRow = (coverage, year) => {
    const updated = support.deleteRow(coverage, data, year);
    dispatch(claimsActions.updateAggregatedClaims({ data: updated }));
  };

  const handleAddRow = (coverage) => {
    const updated = support.addRow(coverage, data);
    dispatch(claimsActions.updateAggregatedClaims({ data: updated }));
  };

  const handleDelete = () => {
    dispatch(
      claimsActions.updateAggregatedClaims({ manualMode: false, data: [] })
    );
  };

  const handleDownload = () => {
    const blob = new Blob([support.toCsvString(data)], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${insured}${insured ? " - " : ""}Loss History.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!usingAggregatedClaimsInput) {
    return <BlankSlate title={title} onAddManually={handleAddManually} />;
  }

  return (
    <Component
      title={title}
      options={
        !coverages.isEditing && (
          <Stack direction={"row"} alignItems={"center"} gap={0.5}>
            <Button
              onClick={() => setCoverages({ ...coverages, isEditing: true })}
              size={"small"}
            >
              {"Edit Coverages"}
            </Button>
            <MoreMenu onDownload={handleDownload} onDelete={handleDelete} />
          </Stack>
        )
      }
    >
      {coverages.isEditing ? (
        <CoveragePicker
          lossCategories={lossCategories}
          coverages={coverages.active}
          onChange={(coverages) => handleUpdateCoverages(coverages)}
          onCancel={() => {
            activeCoverages.length === 0
              ? handleDelete() // Cancel immediately after clicking Add Manually
              : setCoverages({ ...coverages, isEditing: false });
          }}
        />
      ) : (
        <Stack gap={3}>
          {data.map((item) => (
            <Editor
              key={item.coverage}
              lossCategories={lossCategories}
              coverage={item.coverage}
              data={item.data}
              onChange={(newData) => handleUpdateData(item.coverage, newData)}
              onChangeRow={(newRow) => handleUpdateRow(item.coverage, newRow)}
              onDeleteRow={(year) => handleDeleteRow(item.coverage, year)}
              onAddRow={() => handleAddRow(item.coverage)}
            />
          ))}
        </Stack>
      )}
    </Component>
  );
};

export default AggregatedClaimsInput;
